import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { BlogPostBody } from "./components/BlogPostBody";
import { BlogCTA } from "./components/BlogCTA";
import { BlogArticleHero } from "./components/BlogArticleHero";
import { BlogTableOfContents } from "./components/BlogTableOfContents";
import { BlogRelated } from "./components/BlogRelated";
import { BlogEarlyAccessCard } from "./components/BlogEarlyAccessCard";
import { BlogCornerstoneCta } from "./components/BlogCornerstoneCta";
import { ResponsiveCoverImage, type CoverImageVariants } from "@/components/blog/ResponsiveCoverImage";
import { BlogInThisGuide } from "./components/BlogInThisGuide";
import { BlogKeyTakeaway } from "./components/BlogKeyTakeaway";
import { BlogChecklist } from "./components/BlogChecklist";
import { renderMarkdown } from "@/lib/blog/renderMarkdown";
import { buildHeadingIdsAndToc } from "@/lib/blog/toc";
import { estimateReadingTime } from "@/lib/blog/readingTime";
import { inferTopic } from "@/lib/blog/inferTopic";
import {
  normaliseEditorialBlocks,
  hasAnyEditorialContent,
} from "@/lib/blog/editorialBlocks";
import {
  blogPostingJsonLd,
  breadcrumbJsonLd,
  BLOG_SITE_ORIGIN,
} from "@/lib/blog/jsonld";
import { resolvePublicSeo } from "@/lib/blog/resolvePublicSeo";
import {
  pickRecommendations,
  type RecommendationCandidate,
} from "@/lib/blog/recommendations";
import { isTopicCluster, type TopicCluster } from "@/lib/blog/featureRoutes";

interface PublicPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body_md: string;
  cover_image_url: string | null;
  cover_image_variants: CoverImageVariants | null;
  seo_title: string | null;
  canonical_url: string | null;
  published_at: string;
  updated_at: string | null;
  content_updated_at: string | null;
  author_display_name: string;
  editorial_blocks?: unknown;
  topic_cluster?: string | null;
}

interface RelatedRow {
  slug: string;
  title: string;
  excerpt: string | null;
}

interface RelatedQueryRow {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  target_audience: string | null;
  topic_cluster: string | null;
  exclude_from_recommendations: boolean | null;
}

export default function BlogPostPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [post, setPost] = useState<PublicPost | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState<RelatedRow[]>([]);

  useEffect(() => {
    let active = true;
    setPost(null);
    setNotFound(false);
    setRelated([]);
    // Fire both queries in the same tick so the network can overlap them
    // instead of waterfalling. Each await chain previously sat in its own
    // IIFE, which in practice serialised on the shared supabase client.
    const postQuery = supabase
      .from("blog_posts_renderable" as never)
      .select(
        "id, slug, title, excerpt, body_md, cover_image_url, cover_image_variants, seo_title, canonical_url, published_at, updated_at, content_updated_at, author_display_name, editorial_blocks, topic_cluster"
      )
      .eq("slug", slug)
      .maybeSingle();
    // Candidate pool for recommendations. The renderable view already drops
    // unpublished / non-deployed rows, so absence from the result set means
    // "not_renderable_or_missing" for feature-strip classification. The
    // exclude_from_recommendations column tags the locked duplicate
    // (post 04cf49cb-…) so it never appears here but remains in the archive
    // and sitemap. There is NO arbitrary latest-200 cap — a 50-row LIMIT is
    // a query-cost guard, not a recommendation cap.
    const relatedQuery = supabase
      .from("blog_posts_renderable" as never)
      .select(
        "id, slug, title, excerpt, target_audience, topic_cluster, exclude_from_recommendations"
      )
      .neq("slug", slug)
      .order("published_at", { ascending: false })
      .limit(50);
    Promise.all([
      postQuery as unknown as Promise<{ data: PublicPost | null; error: unknown }>,
      relatedQuery as unknown as Promise<{ data: RelatedQueryRow[] | null }>,
    ]).then(
      ([postRes, relatedRes]) => {
        if (!active) return;
        if (postRes.error || !postRes.data) setNotFound(true);
        else setPost(postRes.data);
        const rows = relatedRes.data ?? [];
        const currentId = postRes.data?.id ?? "";
        const currentCluster: TopicCluster | null =
          postRes.data && isTopicCluster(postRes.data.topic_cluster)
            ? (postRes.data.topic_cluster as TopicCluster)
            : null;
        const candidates: RecommendationCandidate[] = rows.map((r) => ({
          id: r.id,
          slug: r.slug,
          title: r.title,
          excerpt: r.excerpt,
          target_audience: r.target_audience,
          topic_cluster: r.topic_cluster,
          eligible: r.exclude_from_recommendations !== true,
        }));
        const picked = pickRecommendations({
          currentId,
          currentCluster,
          candidates,
        });
        setRelated(
          picked.map(({ slug, title, excerpt }) => ({ slug, title, excerpt })),
        );
      },
    );
    return () => {
      active = false;
    };
  }, [slug]);

  if (notFound) {
    // Static Netlify 404.html serves real HTTP 404 for any slug we never
    // prerendered. This client branch covers the rare race where a post
    // is unpublished between deploys — emit noindex, no canonical.
    return (
      <>
        <Helmet>
          <title>Post not found — Youtemp</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="container py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">Post not found</h1>
          <p className="text-muted-foreground mb-6">
            This post is no longer available.
          </p>
          <Link to="/blog/" className="text-primary underline">
            Back to blog
          </Link>
        </div>
      </>
    );
  }

  if (!post) {
    return (
      <div className="container py-20">
        <div className="h-8 w-3/4 bg-muted animate-pulse rounded mb-4" />
        <div className="h-4 w-1/2 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  const {
    finalTitle: pageTitle,
    finalDescription: description,
    finalCanonical: canonical,
    finalOgImage: ogImage,
  } = resolvePublicSeo({
    slug: post.slug,
    title: post.title,
    seo_title: post.seo_title,
    excerpt: post.excerpt,
    cover_image_url: post.cover_image_url,
    canonical_url: post.canonical_url,
  });
  // Twitter requires an absolute URL. resolvePublicSeo's fallback is already
  // absolute; only a custom cover_image_url might be a relative path.
  const absoluteOgImage = /^https?:\/\//i.test(ogImage)
    ? ogImage
    : `${BLOG_SITE_ORIGIN}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;
  const editorial = normaliseEditorialBlocks(post.editorial_blocks);
  const hasEditorial = hasAnyEditorialContent(editorial);

  // Duplicate-CTA guard: only strip an in-body trailing CTA section when the
  // styled BlogCTA is going to render AI-authored override copy. Legacy posts
  // without editorial CTA fields keep their existing in-body CTA untouched.
  const stripTrailingCta = (md: string): string => {
    if (!md) return md;
    const headingRe = /^(#{2,3})\s+([^\n]+)$/gm;
    let last: { index: number; text: string } | null = null;
    let m: RegExpExecArray | null;
    while ((m = headingRe.exec(md)) !== null) {
      last = { index: m.index, text: m[2] };
    }
    if (!last) return md;
    const ctaWords =
      /\b(waitlist|request a demo|book a demo|get started|see youtemp|eager to|ready to|try youtemp|sign up today|get in touch)\b/i;
    return ctaWords.test(last.text) ? md.slice(0, last.index).trimEnd() : md;
  };

  const shouldStripBodyCta = Boolean(
    hasEditorial && (editorial.cta_headline || editorial.cta_body),
  );
  const sourceBodyMd = shouldStripBodyCta
    ? stripTrailingCta(post.body_md ?? "")
    : post.body_md ?? "";

  const rendered = renderMarkdown(sourceBodyMd);
  const { html: bodyHtml, toc: generatedToc } = buildHeadingIdsAndToc(rendered);
  const readingMinutes = estimateReadingTime(post.body_md);
  const topic = inferTopic({ title: post.title, excerpt: post.excerpt });

  // TOC: generatedToc (real injected H2 ids) is the source of truth. If AI
  // editorial.toc labels exist, use them only to re-order/relabel by mapping
  // to real generated headings via normalised text. Fall back to generatedToc
  // when fewer than 2 AI labels resolve to real H2s.
  const norm = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  let tocItems = generatedToc;
  if (editorial.toc && editorial.toc.length > 0 && generatedToc.length > 0) {
    const byNorm = new Map(generatedToc.map((t) => [norm(t.text), t]));
    const mapped = editorial.toc
      .map((label) => {
        const n = norm(label);
        const exact = byNorm.get(n);
        if (exact) return exact;
        // unambiguous substring fallback
        const matches = generatedToc.filter(
          (t) => norm(t.text).includes(n) || n.includes(norm(t.text)),
        );
        return matches.length === 1 ? matches[0] : undefined;
      })
      .filter((x): x is (typeof generatedToc)[number] => Boolean(x));
    if (mapped.length >= 2) tocItems = mapped;
  }

  // Duplicate-checklist guard: if the body already has an H2/H3 with the word
  // "checklist", suppress the styled BlogChecklist component.
  const bodyHasChecklistHeading = /^\s*#{2,3}\s+[^\n]*\bchecklist\b/im.test(
    post.body_md ?? "",
  );
  const showChecklist =
    hasEditorial &&
    !!editorial.practical_checklist &&
    editorial.practical_checklist.length > 0 &&
    !bodyHasChecklistHeading;

  const postingLd = blogPostingJsonLd({
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt,
    coverImageUrl: post.cover_image_url,
    publishedAt: post.published_at,
    updatedAt: post.content_updated_at,
    authorName: post.author_display_name,
    canonical,
  });

  const crumbsLd = breadcrumbJsonLd([
    { name: "Home", url: `${BLOG_SITE_ORIGIN}/` },
    { name: "Blog", url: `${BLOG_SITE_ORIGIN}/blog/` },
    { name: post.title, url: `${BLOG_SITE_ORIGIN}/blog/${post.slug}/` },
  ]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage} />
        <meta property="article:published_time" content={post.published_at} />
        {post.content_updated_at && (
          <meta property="article:modified_time" content={post.content_updated_at} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={absoluteOgImage} />
        <script type="application/ld+json">{JSON.stringify(postingLd)}</script>
        <script type="application/ld+json">{JSON.stringify(crumbsLd)}</script>
      </Helmet>

      <article className="container max-w-7xl section-py">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10">
          {/* Left column: hero, cover, body, CTA, related — all aligned to
              the same left edge for layout rhythm consistent with /blog/. */}
          <div className="min-w-0">
            <BlogArticleHero
              topic={topic}
              title={post.title}
              excerpt={post.excerpt}
              publishedAt={post.published_at}
              updatedAt={post.content_updated_at}
              readingMinutes={readingMinutes}
              authorName={post.author_display_name}
            />

            <div className="rounded-2xl overflow-hidden border border-border mb-10 shadow-sm aspect-[16/9] bg-muted">
              <ResponsiveCoverImage
                variants={post.cover_image_variants}
                src={post.cover_image_url}
                alt={post.title}
                slug={post.slug}
                priority
                sizes="(min-width: 1280px) 880px, (min-width: 1024px) 60vw, 100vw"
                imgClassName="w-full h-full object-cover"
              />
            </div>

            {hasEditorial && editorial.in_this_guide && (
              <BlogInThisGuide items={editorial.in_this_guide} />
            )}
            {hasEditorial && editorial.key_takeaway && (
              <BlogKeyTakeaway text={editorial.key_takeaway} />
            )}
            <BlogPostBody html={bodyHtml} />
            {showChecklist && (
              <BlogChecklist items={editorial.practical_checklist!} />
            )}
            <BlogCTA
              title={editorial.cta_headline}
              description={editorial.cta_body}
            />
            <BlogCornerstoneCta cluster={post.topic_cluster} />
            <BlogRelated posts={related} />
          </div>

          {/* Right column (desktop only): sticky stack of TOC + early-access
              card. Wrapping the sticky here (instead of on the TOC itself)
              lets both cards travel together and keeps the column visually
              balanced even when the TOC is short. */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <BlogTableOfContents items={tocItems} />
              <BlogEarlyAccessCard />
            </div>
          </aside>
        </div>
      </article>
    </>
  );
}